import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Download, Trash2, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNewsletter({ user, setUser }) {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchSubscribers();
  }, [user, navigate]);

  const fetchSubscribers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/newsletter/subscribers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSubscribers(data.subscribers || []);
      setStats({
        total: data.total || 0,
        active: data.active || 0,
        unsubscribed: data.unsubscribed || 0
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar suscriptores');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriberId) => {
    if (!confirm('¿Eliminar este suscriptor?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/newsletter/subscribers/${subscriberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Suscriptor eliminado');
        fetchSubscribers();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/newsletter/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Download CSV
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `newsletter_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`${data.count} suscriptores exportados`);
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black">Avisos</Link>
            <Link to="/admin/newsletter" className="font-medium text-black border-b-2 border-black pb-2">Newsletter</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Suscriptores Newsletter</h2>
          <Button onClick={handleExport} className="bg-black text-white">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border-2 border-black">
            <div className="flex items-center justify-between mb-2">
              <Mail className="w-8 h-8" />
              <span className="text-3xl font-bold">{stats.total}</span>
            </div>
            <p className="text-gray-600">Total Suscriptores</p>
          </div>
          <div className="bg-white p-6 border-2 border-green-500">
            <div className="flex items-center justify-between mb-2">
              <Mail className="w-8 h-8 text-green-600" />
              <span className="text-3xl font-bold text-green-600">{stats.active}</span>
            </div>
            <p className="text-gray-600">Activos</p>
          </div>
          <div className="bg-white p-6 border-2 border-gray-300">
            <div className="flex items-center justify-between mb-2">
              <Mail className="w-8 h-8 text-gray-400" />
              <span className="text-3xl font-bold text-gray-400">{stats.unsubscribed}</span>
            </div>
            <p className="text-gray-600">Dados de Baja</p>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white border-2 border-black">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Nombre</th>
                  <th className="text-left p-4">Estado</th>
                  <th className="text-left p-4">Fecha</th>
                  <th className="text-right p-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length > 0 ? (
                  subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="border-b">
                      <td className="p-4 font-medium">{subscriber.email}</td>
                      <td className="p-4">{subscriber.name || '-'}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          subscriber.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {subscriber.status === 'active' ? 'Activo' : 'Dado de baja'}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        {new Date(subscriber.subscribed_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(subscriber.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500">
                      <Mail className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                      <p>No hay suscriptores aún</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}